
















































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import AuthModule from '@/store/AuthModule';

@Component({
    filters: {
        formatOptionStatus(status: string) {
            const map: Record<string, string> = {
                accepted: 'Acceptée',
                pending:  'En attente',
                refused:  'Refusée',
                canceled: 'Annulée',
            };
            return map[status] || status;
        },
        formatPrice(value: number) {
            if (!value) return '—';
            return value.toLocaleString('fr-FR');
        },
    },
})
export default class OptionsReportModal extends Vue {
    @Prop({ required: true }) options!: any[];

    step: string = 'confirm';
    progress: number = 0;
    progressMessage: string = '';

    progressSteps = [
        { at: 10,  label: 'Préparation des données...' },
        { at: 30,  label: 'Analyse des options...' },
        { at: 55,  label: 'Construction du document...' },
        { at: 80,  label: 'Mise en forme du PDF...' },
        { at: 95,  label: 'Ouverture du rapport...' },
    ];

    countByStatus(status: string): number {
        return this.options.filter(o => o.status === status).length;
    }

    get currentUserName(): string {
        return `${AuthModule.firstname || ''} ${AuthModule.lastname || ''}`.trim() || 'N/A';
    }

    get currentUserPhone(): string {
        return (AuthModule as any).mobile || (AuthModule as any).phone || '';
    }

    get currentUserCompany(): string {
        const c = (AuthModule as any).company;
        return c && c.name ? c.name : '';
    }

    sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async animateTo(target: number, label: string, ms: number) {
        this.progressMessage = label;
        const step = (target - this.progress) / 10;
        for (let i = 0; i < 10; i++) {
            await this.sleep(ms / 10);
            this.progress = Math.min(target, Math.round(this.progress + step));
        }
    }

    async startGeneration() {
        this.step = 'generating';
        this.progress = 0;

        await this.animateTo(10,  'Préparation des données...', 400);
        await this.animateTo(30,  'Analyse des options...', 500);
        await this.animateTo(55,  'Construction du document...', 600);

        const html = this.buildPdfHtml();

        await this.animateTo(80,  'Mise en forme du PDF...', 400);
        await this.animateTo(95,  'Ouverture du rapport...', 300);

        const win = window.open('', '_blank');
        if (win) {
            win.document.write(html);
            win.document.close();
            win.addEventListener('load', function() {
                setTimeout(function() {
                    win.print();
                }, 2000);
            });
        }

        await this.animateTo(100, 'Terminé !', 200);
        await this.sleep(400);
        this.step = 'done';
    }

    formatStatus(status: string): string {
        const map: Record<string, string> = {
            accepted: 'Acceptée',
            pending:  'En attente',
            refused:  'Refusée',
            canceled: 'Annulée',
        };
        return map[status] || status;
    }

    formatLotStatus(status: string): string {
        const map: Record<string, string> = {
            available: 'Disponible',
            optionned: 'Optionné',
            reserved:  'Réservé',
            booked:    'Réservé',
            acted:     'Acté',
            retired:   'Retiré',
            sold:      'Vendu',
            nc:        'NC',
        };
        return map[status] || status || '—';
    }

    formatPrice(value: number): string {
        if (!value) return '—';
        return value.toLocaleString('fr-FR') + ' €';
    }

    optionStatusColor(status: string): string {
        const map: Record<string, string> = {
            accepted: '#22c55e',
            pending:  '#f59e0b',
            refused:  '#ef4444',
            canceled: '#94a3b8',
        };
        return map[status] || '#94a3b8';
    }

    optionStatusBg(status: string): string {
        const map: Record<string, string> = {
            accepted: '#f0fdf4',
            pending:  '#fffbeb',
            refused:  '#fef2f2',
            canceled: '#f8fafc',
        };
        return map[status] || '#f8fafc';
    }

    lotStatusColor(status: string): string {
        const map: Record<string, string> = {
            available: '#22c55e',
            optionned: '#f59e0b',
            reserved:  '#3b82f6',
            booked:    '#3b82f6',
            acted:     '#8b5cf6',
            retired:   '#94a3b8',
            sold:      '#64748b',
        };
        return map[status] || '#94a3b8';
    }

    safeHtml(text: string): string {
        const decoder = document.createElement('div');
        decoder.innerHTML = text || '';
        const decoded = decoder.textContent || '';
        const encoder = document.createElement('div');
        encoder.textContent = decoded;
        return encoder.innerHTML;
    }

    buildPdfHtml(): string {
        const date = new Date().toLocaleDateString('fr-FR', {
            day: '2-digit', month: 'long', year: 'numeric',
        });
        const userName    = this.safeHtml(this.currentUserName);
        const userPhone   = this.safeHtml(this.currentUserPhone);
        const userCompany = this.safeHtml(this.currentUserCompany);

        const accepted = this.countByStatus('accepted');
        const pending  = this.countByStatus('pending');
        const refused  = this.countByStatus('refused');
        const canceled = this.countByStatus('canceled');

        /* ── Table rows ── */
        const rows = this.options.map((opt, idx) => {
            const statusLabel    = this.formatStatus(opt.status);
            const sColor         = this.optionStatusColor(opt.status);
            const sBg            = this.optionStatusBg(opt.status);
            const lotStatusLabel = this.formatLotStatus(opt.lotStatus);
            const lColor         = this.lotStatusColor(opt.lotStatus);
            const price          = this.formatPrice(opt.price);
            const surface        = opt.surface ? opt.surface + ' m²' : '—';
            const prospect       = this.safeHtml(opt.prospect || '—');
            const programme      = this.safeHtml(opt.programmeName || 'N° ' + opt.programmeId);
            const address        = this.safeHtml(opt.address || '—');
            const lotName        = this.safeHtml(opt.lotName || '—');
            const lotType        = this.safeHtml(opt.lotType || '—');
            const endAt          = this.safeHtml(opt.endAt || '—');
            const updatedAt      = opt.updatedAt ? this.safeHtml(opt.updatedAt) : '';
            const bookingBadge   = opt.bookingStatus
                ? `<span class="booking-badge">${this.safeHtml(opt.bookingStatus)}</span>`
                : '';

            const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';

            return `
                <tr style="background:${rowBg};">
                    <td class="td-num">${idx + 1}</td>
                    <td class="td-prospect">
                        <span class="prospect-avatar">${prospect.charAt(0).toUpperCase()}</span>
                        ${prospect}
                    </td>
                    <td class="td-programme">${programme}</td>
                    <td class="td-address">${address}</td>
                    <td class="td-lot"><span class="lot-chip">${lotName}</span></td>
                    <td class="td-center">${lotType}</td>
                    <td class="td-center">${surface}</td>
                    <td class="td-right td-price">${price}</td>
                    <td class="td-center">
                        <span class="ls-badge" style="color:${lColor};border-color:${lColor};">${lotStatusLabel}</span>
                    </td>
                    <td class="td-center">
                        <span class="opt-badge" style="color:${sColor};background:${sBg};border-color:${sColor};">
                            ${statusLabel}
                        </span>
                        ${updatedAt ? `<div class="opt-date">${updatedAt}</div>` : ''}
                        ${bookingBadge}
                    </td>
                    <td class="td-center td-expiry">${endAt}</td>
                </tr>`;
        }).join('');

        return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Rapport Options — ${date}</title>
<style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    @page {
        size: A4 landscape;
        margin: 12mm 14mm 12mm 14mm;
    }

    body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
        font-size: 9.5px;
        color: #1e293b;
        background: #fff;
        line-height: 1.4;
    }

    /* ── Header ── */
    .pdf-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding-bottom: 12px;
        border-bottom: 3px solid #1e40af;
        margin-bottom: 14px;
    }
    .pdf-title {
        font-size: 22px;
        font-weight: 800;
        color: #1e40af;
        letter-spacing: -0.5px;
        line-height: 1.1;
    }
    .pdf-subtitle {
        font-size: 10.5px;
        color: #64748b;
        margin-top: 3px;
    }
    .pdf-header-right {
        text-align: right;
        font-size: 9px;
        color: #475569;
        line-height: 1.7;
    }
    .pdf-header-right strong {
        display: block;
        font-size: 11px;
        color: #1e293b;
        font-weight: 700;
    }
    .pdf-date {
        display: inline-block;
        margin-top: 4px;
        padding: 2px 8px;
        background: #f1f5f9;
        border-radius: 20px;
        font-size: 8.5px;
        color: #64748b;
    }

    /* ── Summary bar ── */
    .summary-bar {
        display: flex;
        gap: 0;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 16px;
    }
    .summary-item {
        flex: 1;
        text-align: center;
        padding: 9px 6px;
        border-right: 1px solid #e2e8f0;
    }
    .summary-item:last-child { border-right: none; }
    .summary-val {
        display: block;
        font-size: 18px;
        font-weight: 800;
        color: #1e40af;
        line-height: 1;
    }
    .summary-lbl {
        display: block;
        font-size: 8px;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-top: 2px;
    }
    .summary-val.green  { color: #22c55e; }
    .summary-val.orange { color: #f59e0b; }
    .summary-val.red    { color: #ef4444; }
    .summary-val.gray   { color: #94a3b8; }

    /* ── Main table ── */
    .options-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 8.5px;
    }
    .options-table thead tr {
        background: #1e40af;
        color: #fff;
    }
    .options-table th {
        padding: 6px 7px;
        font-weight: 600;
        text-align: left;
        font-size: 8px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        white-space: nowrap;
    }
    .options-table th.th-center { text-align: center; }
    .options-table th.th-right  { text-align: right; }

    .options-table tbody tr { border-bottom: 1px solid #f1f5f9; }
    .options-table td { padding: 6px 7px; vertical-align: middle; }

    .td-num     { color: #94a3b8; font-size: 8px; font-weight: 600; width: 20px; }
    .td-center  { text-align: center; }
    .td-right   { text-align: right; }

    .td-prospect {
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        color: #0f172a;
        white-space: nowrap;
    }
    .prospect-avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #e0e7ff;
        color: #3730a3;
        font-size: 8px;
        font-weight: 800;
        flex-shrink: 0;
    }
    .td-programme { font-weight: 600; color: #1e40af; }
    .td-address   { color: #64748b; font-size: 8px; max-width: 140px; }
    .td-price     { font-weight: 700; color: #0f172a; font-size: 9px; white-space: nowrap; }
    .td-expiry    { color: #64748b; white-space: nowrap; }

    .lot-chip {
        display: inline-block;
        padding: 2px 7px;
        background: #e0e7ff;
        color: #3730a3;
        border-radius: 6px;
        font-weight: 700;
        font-size: 8px;
        white-space: nowrap;
    }

    .ls-badge {
        display: inline-block;
        padding: 2px 6px;
        border-radius: 10px;
        border: 1px solid;
        font-size: 7.5px;
        font-weight: 600;
        white-space: nowrap;
    }
    .opt-badge {
        display: inline-block;
        padding: 2px 7px;
        border-radius: 10px;
        border: 1px solid;
        font-size: 7.5px;
        font-weight: 700;
        white-space: nowrap;
    }
    .opt-date {
        font-size: 7px;
        color: #94a3b8;
        margin-top: 2px;
    }
    .booking-badge {
        display: inline-block;
        padding: 1px 5px;
        border-radius: 6px;
        background: #ede9fe;
        color: #6d28d9;
        font-size: 7px;
        font-weight: 600;
        margin-top: 2px;
    }

    /* ── Footer ── */
    .pdf-footer {
        margin-top: 16px;
        padding-top: 8px;
        border-top: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        font-size: 7.5px;
        color: #94a3b8;
    }
    .pdf-footer-brand { font-weight: 600; color: #64748b; }
</style>
</head>
<body>

<!-- ── Header ── -->
<div class="pdf-header">
    <div>
        <div class="pdf-title">Rapport des Options</div>
        <div class="pdf-subtitle">${this.options.length} option(s) sélectionnée(s) · ${date}</div>
    </div>
    <div class="pdf-header-right">
        <strong>${userName}</strong>
        ${userCompany ? `<span>${userCompany}</span>` : ''}
        ${userPhone   ? `<span>${userPhone}</span>`   : ''}
        <span class="pdf-date">Généré le ${date}</span>
    </div>
</div>

<!-- ── Summary ── -->
<div class="summary-bar">
    <div class="summary-item">
        <span class="summary-val">${this.options.length}</span>
        <span class="summary-lbl">Total</span>
    </div>
    <div class="summary-item">
        <span class="summary-val green">${accepted}</span>
        <span class="summary-lbl">Acceptées</span>
    </div>
    <div class="summary-item">
        <span class="summary-val orange">${pending}</span>
        <span class="summary-lbl">En attente</span>
    </div>
    <div class="summary-item">
        <span class="summary-val red">${refused}</span>
        <span class="summary-lbl">Refusées</span>
    </div>
    <div class="summary-item">
        <span class="summary-val gray">${canceled}</span>
        <span class="summary-lbl">Annulées</span>
    </div>
</div>

<!-- ── Table ── -->
<table class="options-table">
    <thead>
        <tr>
            <th>#</th>
            <th>Prospect</th>
            <th>Programme</th>
            <th>Adresse</th>
            <th>Lot</th>
            <th class="th-center">Type</th>
            <th class="th-center">Surface</th>
            <th class="th-right">Prix TTC</th>
            <th class="th-center">Statut Lot</th>
            <th class="th-center">Statut Option</th>
            <th class="th-center">Expiration</th>
        </tr>
    </thead>
    <tbody>
        ${rows}
    </tbody>
</table>

<!-- ── Footer ── -->
<div class="pdf-footer">
    <span class="pdf-footer-brand">Plugimmo</span>
    <span>Rapport confidentiel · ${date}</span>
</div>

</body>
</html>`;
    }
}
